import { Grid01, Settings04 } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getOrderStatusBadgeType } from 'src/app/Orders/order.helpers';
import ButtonCmp from 'src/components/ButtonCmp';
import CardCmp from 'src/components/CardCmp';
import NotFoundUI from 'src/components/NotFoundUI';
import TooltipCmp from 'src/components/TooltipCmp';
import WidgetSettingsModal from 'src/components/WidgetSettingsModal';
import { IWidget } from 'src/components/WidgetSettingsModal/widgetSettings.interface';
import { ROUTES } from 'src/constants/routes';
import { setCurrentUser } from 'src/redux/auth.slice';
import { orderDetailsData, orderDetailsLoading, selectCurrentLocation } from 'src/redux/orders.slice';
import { RootState } from 'src/redux/store';
import { updateUserWidgets } from 'src/services/AuthService';
import { transformWidgetsToApiFormat, WIDGET_SECTION_TYPE } from 'src/utils/widgetSettings.utils';

import MapItWidget from '../../MyQuote/QuoteDetail/MapIt';

import DocumentsWidget from './DocumentsWidget';
import MessagesWidget from './MessagesWidget';
import { DEFAULT_WIDGET_SETTINGS, ORDER_WIDGET_IDS, transformWidgets, Widget } from './orderWidgetSettings.helper';
import TermsAndConditions from './TermsAndConditions';

interface IOrderDetailsWidgetsProps {
    showCustomize?: boolean;
    onOpenInRadarClick?: () => void;
}

const OrderDetailsWidgets = ({ showCustomize = true, onOpenInRadarClick }: IOrderDetailsWidgetsProps) => {
    const dispatch = useDispatch();
    const { id } = useParams();

    const order = useSelector(orderDetailsData);
    const loading = useSelector(orderDetailsLoading);
    const currentLocation = useSelector(selectCurrentLocation);
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);

    // Derive map start/end points and waypoints from order data (mirrors reference mapIt.helper logic)
    const [isWidgetsModalOpen, setIsWidgetsModalOpen] = useState(false);

    const widgetsContentRef = useRef<HTMLDivElement>(null);

    const scrollToTop = useCallback(() => {
        if (widgetsContentRef.current) {
            widgetsContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, []);

    const widgets = useMemo<Widget[]>(() => {
        const settings = currentUser?.orderWidgetSettings ?? DEFAULT_WIDGET_SETTINGS;

        // Existing users have saved settings without the messages key — inject at last position
        if (currentUser?.orderWidgetSettings && !(ORDER_WIDGET_IDS.MESSAGES in settings)) {
            const maxPosition = Object.values(settings as Record<string, { isEnabled: boolean; position: number }>).reduce((max, w) => Math.max(max, w.position), -1);

            return transformWidgets({
                ...settings,
                [ORDER_WIDGET_IDS.MESSAGES]: { isEnabled: true, position: maxPosition + 1 },
            });
        }

        return transformWidgets(settings);
    }, [currentUser?.orderWidgetSettings]);

    const isWidgetsDisabled = useMemo(() => {
        if (!widgets.length) return false;

        return widgets.every((widget) => !widget.isEnabled);
    }, [widgets]);

    // Derive map start/end points and waypoints from order data
    const mapData = useMemo(() => {
        const shipperAddress = order?.shipper?.shipperAddress;
        const consigneeAddress = order?.consignee?.consigneeAddress;

        const startPoint =
            shipperAddress?.latitude && shipperAddress?.longitude
                ? {
                      lat: parseFloat(shipperAddress.latitude),
                      lng: parseFloat(shipperAddress.longitude),
                      name: shipperAddress?.city && shipperAddress?.state ? `${shipperAddress.city}, ${shipperAddress.state}` : null,
                  }
                : null;

        const endPoint =
            consigneeAddress?.latitude && consigneeAddress?.longitude
                ? {
                      lat: parseFloat(consigneeAddress.latitude),
                      lng: parseFloat(consigneeAddress.longitude),
                      name: consigneeAddress?.city && consigneeAddress?.state ? `${consigneeAddress.city}, ${consigneeAddress.state}` : null,
                  }
                : null;

        const preventCrossBorder = !!(shipperAddress?.country && consigneeAddress?.country && shipperAddress.country === consigneeAddress.country);

        const waypoints =
            order?.orderLocationUpdateList && order.orderLocationUpdateList.length > 0
                ? order.orderLocationUpdateList
                      .filter((loc: any) => loc?.latitude && loc?.longitude)
                      .map((loc: any) => ({ lat: parseFloat(loc.latitude), lng: parseFloat(loc.longitude), name: loc?.city || null }))
                      .reverse()
                : [];

        const locationUpdate = order?.orderLocationUpdate;
        // After .reverse(), the array is ASC by createdAt — the most recent tracked
        // location is at the END (last index), not the beginning. Using waypoints[0]
        // here would yield the OLDEST update (near the start point), which makes the
        // current-location marker render at the wrong place. Pick the last entry instead.
        const latestWaypoint = waypoints.length > 0 ? waypoints[waypoints.length - 1] : null;

        // Redux's `currentLocation` is hydrated from `orderLocationUpdateList[0]` (the raw
        // backend object), so it has `latitude`/`longitude` as strings — not `lat`/`lng`.
        // Handle both shapes so socket-pushed updates AND initial hydration both work.
        const resolvedCurrentLocation =
            currentLocation?.latitude && currentLocation?.longitude
                ? { lat: parseFloat(currentLocation.latitude), lng: parseFloat(currentLocation.longitude) }
                : currentLocation?.lat && currentLocation?.lng
                ? currentLocation
                : locationUpdate?.latitude && locationUpdate?.longitude
                ? { lat: parseFloat(locationUpdate.latitude), lng: parseFloat(locationUpdate.longitude) }
                : latestWaypoint
                ? { lat: latestWaypoint.lat, lng: latestWaypoint.lng }
                : startPoint
                ? { lat: startPoint.lat, lng: startPoint.lng }
                : null;

        const orderStatus = order?.orderStatus;
        const progressPercent = order?.orderLocationUpdateList?.[0]?.progressPercentage ?? order?.progressPercent ?? 0;
        const statusInfo = orderStatus
            ? {
                  progressPercent: `${progressPercent}%`,
                  status: orderStatus,
                  statusType: getOrderStatusBadgeType(orderStatus),
              }
            : undefined;

        return { startPoint, endPoint, preventCrossBorder, waypoints, currentLocation: resolvedCurrentLocation, statusInfo };
        // Deps are the nested order slices this memo actually reads — NOT the `order` object itself.
        // updateOrderField (e.g. Mark as Priority) replaces the top-level order via spread, but these
        // nested references stay stable, so the map keeps identical props and doesn't clear/refetch
        // its route (visible blink). Depending on `order` remounts the route on every unrelated field change.
    }, [order?.shipper?.shipperAddress, order?.consignee?.consigneeAddress, order?.orderLocationUpdateList, order?.orderLocationUpdate, order?.orderStatus, order?.progressPercent, currentLocation]);

    const handleSaveWidgets = useCallback(
        async (updatedWidgets: IWidget[]) => {
            const widgetsObject = transformWidgetsToApiFormat(updatedWidgets);

            const payload = {
                type: WIDGET_SECTION_TYPE.ORDER,
                settings: widgetsObject,
            };

            const response = await updateUserWidgets(payload);

            scrollToTop();

            dispatch(
                setCurrentUser({
                    ...currentUser,
                    orderWidgetSettings: response?.data?.orderWidgetSettings ?? widgetsObject,
                })
            );
        },
        [dispatch, currentUser, scrollToTop]
    );

    return (
        <>
            <div className={`widget-right-sidebar-common-content-x-spacing ${!showCustomize && 'max-lg:!px-0 lg:!pr-1'}`}>
                <div className="border-b border-borderPrimary w-full pb-1.5 flex items-center justify-between min-h-[35px]">
                    <h6 className="text-textSecondary text-sm font-semibold">Widgets</h6>
                    {showCustomize && (
                        <TooltipCmp message="Customize Widgets">
                            <ButtonCmp className="btn_secondary_black" size="square-sm" onClick={() => setIsWidgetsModalOpen(true)}>
                                <Settings04 className="w-4 h-4" />
                            </ButtonCmp>
                        </TooltipCmp>
                    )}
                </div>
            </div>

            {isWidgetsDisabled ? (
                <div className={`widget-right-sidebar-common-content widget-right-sidebar-common-content-x-spacing !mt-0 ${!showCustomize && 'max-lg:!px-0 lg:!pr-1'}`}>
                    <div className="table-box-wrap min-h-[192px] flex items-center justify-center">
                        <NotFoundUI
                            title="No Widgets Added"
                            desc="Enable widgets from the settings to see them here."
                            iconClassName="!hidden"
                            iconSecondaryClassName="!hidden"
                            descClass="!px-0"
                            containerClassName="!my-[0px] !h-auto"
                            contentClassName="before:!bg-none"
                            iconSecondary={<Grid01 className="w-5 h-5 text-primary" />}
                        />
                    </div>
                </div>
            ) : (
                <div
                    className={`widget-right-sidebar-common-content widget-right-sidebar-common-content-x-spacing !mt-0 ${!showCustomize && 'max-lg:!px-0 lg:!pr-1 max-lg:!pb-[100px]'}`}
                    ref={widgetsContentRef}
                >
                    {widgets.map((widget, key) => (
                        <React.Fragment key={key}>
                            {widget.id === ORDER_WIDGET_IDS.MAP_IT && widget.isEnabled && (
                                <MapItWidget
                                    title="Tracking"
                                    startPoint={mapData.startPoint}
                                    endPoint={mapData.endPoint}
                                    waypoints={mapData.waypoints}
                                    currentLocation={mapData.currentLocation}
                                    preventCrossBorder={mapData.preventCrossBorder}
                                    showOpenButton={true}
                                    openButtonLabel="Open in Radar"
                                    onOpenClick={
                                        onOpenInRadarClick ??
                                        (() => {
                                            if (id) window.open(`${ROUTES.TRACK}/${id}`, '_blank', 'noopener,noreferrer');
                                        })
                                    }
                                    isLoading={loading}
                                />
                            )}

                            {widget.id === ORDER_WIDGET_IDS.DOCUMENTS && widget.isEnabled && <DocumentsWidget isReadOnly={!showCustomize} />}

                            {widget.id === ORDER_WIDGET_IDS.TERMS_AND_CONDITION && widget.isEnabled && (
                                <CardCmp
                                    headerTitle="Terms & Conditions"
                                    headerParentClassName="!rounded-t-lg !px-0 !mx-3"
                                    isCardContentWithoutSpacing={true}
                                    childrenClassName="widget-card-content-wrap"
                                >
                                    <TermsAndConditions />
                                </CardCmp>
                            )}

                            {/* Chat is unavailable for WAL staff sessions (no contact participant row) */}
                            {widget.id === ORDER_WIDGET_IDS.MESSAGES &&
                                widget.isEnabled &&
                                !currentUser?.isWalUserSession &&
                                (showCustomize ? <MessagesWidget orderId={id || ''} mainLoading={loading} /> : <></>)}
                        </React.Fragment>
                    ))}
                </div>
            )}

            {showCustomize && isWidgetsModalOpen && <WidgetSettingsModal handleClose={() => setIsWidgetsModalOpen(false)} widgets={widgets} onSave={handleSaveWidgets} />}
        </>
    );
};

export default OrderDetailsWidgets;
